// crushhh/src/components/PushNotificationsModal.tsx
import React, { useEffect, useRef } from "react";

interface PushNotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PushNotificationsModal: React.FC<PushNotificationsModalProps> = ({ isOpen, onClose }) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const titleId = "push-notifications-title";
  const descId = "push-notifications-desc";

  useEffect(() => {
    if (!isOpen) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={onOverlayClick}
      className="
        fixed inset-0 z-50 bg-black/50 backdrop-blur-[1px]
        overflow-y-auto no-scrollbar p-4
        [scrollbar-gutter:stable]
      "
      role="presentation"
    >
      <div className="min-h-full flex items-center justify-center">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descId}
          className="animate-[scaleIn_120ms_ease-out]"
          style={{
            background: "#FFFFFF",
            border: "3px solid #1A1A1A",
            borderRadius: 20,
            boxShadow: "6px 6px 0px #1A1A1A",
            maxWidth: 380,
            width: "90%",
            overflow: "hidden",
            maxHeight: "85vh",
            overflowY: "auto",
            boxSizing: "border-box",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#FFFFFF",
              padding: "20px 24px 12px",
              textAlign: "center",
              borderBottom: "1px solid rgba(26,26,26,0.08)",
            }}
          >
            <h2
              id={titleId}
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 22,
                fontWeight: 700,
                color: "#1A1A1A",
                margin: 0,
              }}
            >
              Push Notifications
            </h2>
          </div>

          {/* Body */}
          <div
            id={descId}
            style={{
              background: "#FFFFFF",
              padding: "16px 24px 20px",
            }}
          >
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 14,
                color: "#444",
                marginTop: 0,
                marginBottom: 16,
              }}
            >
              Stay in the loop with{" "}
              <strong style={{ color: "#E05A3A", fontWeight: 700 }}>
                CRUSHHH Push Notifications
              </strong>{" "}
              so you never miss an important moment!
            </p>

            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {[
                "Someone adds you to their crush list",
                "A mutual match is discovered!",
                "Important security or account updates",
              ].map((text, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: 14,
                    color: "#1A1A1A",
                    marginBottom: 8,
                  }}
                >
                  <svg
                    style={{
                      marginTop: 2,
                      height: 20,
                      width: 20,
                      flex: "none",
                      color: "#E05A3A",
                    }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 0 1 0 1.414l-7.25 7.25a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414l2.293 2.293 6.543-6.543a1 1 0 0 1 1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{text}</span>
                </li>
              ))}
            </ul>

            <div>
              <h4
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#1A1A1A",
                  marginTop: 16,
                  marginBottom: 8,
                }}
              >
                How it works
              </h4>
              <ul
                style={{
                  listStyle: "disc",
                  paddingLeft: 20,
                  margin: 0,
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: "#555",
                }}
              >
                <li style={{ marginBottom: 4 }}>
                  <strong>ON</strong>: Real-time alerts when something exciting happens.
                </li>
                <li>
                  <strong>OFF</strong>: No alerts; updates will still be there when you log in.
                </li>
              </ul>
              <p
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: "#444",
                  marginTop: 12,
                  marginBottom: 0,
                }}
              >
                <strong style={{ color: "#E05A3A" }}>Not a fan of notifications?</strong> Change in settings.
              </p>
            </div>

            <button
              ref={closeRef}
              onClick={onClose}
              style={{
                background: "#E05A3A",
                color: "#FFFFFF",
                border: "3px solid #1A1A1A",
                borderRadius: 100,
                boxShadow: "4px 4px 0px #1A1A1A",
                padding: "13px 24px",
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 18,
                fontWeight: 700,
                width: "100%",
                marginTop: 16,
                cursor: "pointer",
                boxSizing: "border-box",
                transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
              }}
            >
              Got it
            </button>
          </div>
        </div>
      </div>

      {/* tiny keyframes */}
      <style>{`
        @keyframes scaleIn {
          from { transform: scale(.96); opacity: .8 }
          to   { transform: scale(1);   opacity: 1 }
        }
      `}</style>
    </div>
  );
};

export default PushNotificationsModal;
